import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { loescheOrdner } from 'modules/wissen/features/wissenSlice';
import { ordnerPfad, zaehleOrdnerInhalt } from 'modules/wissen/components/wissenHelpers';
import OrdnerKachel from 'modules/wissen/components/OrdnerKachel';
import NotizBoard from 'modules/wissen/components/NotizBoard';
import DateienPanel from 'modules/wissen/components/DateienPanel';
import OrdnerDialog from 'modules/wissen/components/OrdnerDialog';

const ANSICHTEN = [
  { id: 'ordner', label: '📁 Ordner' },
  { id: 'favoriten', label: '⭐ Favoriten' },
  { id: 'dateien', label: '📎 Dateien' },
];

/**
 * Karten-basierter Browser fuer den Wissensbereich: Ordner als Kacheln
 * (Drill-down mit Breadcrumb), darunter der dynamische Notiz-Bereich. Ansicht-
 * Chips fuer Favoriten und freie Dateien. Oeffnen/Anlegen von Notizen laeuft
 * ueber die Callbacks der Seite (Vollbild-Editor).
 */
export default function WissenBrowser({ notizen, ordner, dateien, onOeffnenNotiz, onNeueNotiz }) {
  const dispatch = useDispatch();
  const [aktuellerOrdnerId, setAktuellerOrdnerId] = useState(null);
  const [suche, setSuche] = useState('');
  const [ansicht, setAnsicht] = useState('ordner');
  const [dialog, setDialog] = useState(null); // { ordner?, presetParentId? } | null

  const q = suche.trim().toLowerCase();
  const passtZurSuche = (n) => {
    if (!q) return true;
    return `${n.titel} ${n.text} ${(n.tags || []).join(' ')}`.toLowerCase().includes(q);
  };

  const unterordner = useMemo(
    () =>
      ordner
        .filter((o) => (o.parentId ?? null) === aktuellerOrdnerId)
        .sort((a, b) => (a.sortierung - b.sortierung) || a.name.localeCompare(b.name)),
    [ordner, aktuellerOrdnerId]
  );

  const pfad = ordnerPfad(ordner, aktuellerOrdnerId);

  const loeschenOrdner = (o) => {
    if (window.confirm(`Ordner „${o.name}" löschen? Unterordner werden ebenfalls entfernt; Notizen und Dateien bleiben erhalten.`)) {
      dispatch(loescheOrdner(o.uuid));
    }
  };

  return (
    <section className="animate-fade-in-up">
      {/* Steuerleiste */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-xl bg-paper-soft p-1">
          {ANSICHTEN.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setAnsicht(a.id)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                ansicht === a.id ? 'bg-paper text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>

        {ansicht !== 'dateien' && (
          <input
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Notizen durchsuchen…"
            className="min-w-[12rem] flex-1 rounded-lg border border-line bg-paper px-3 py-2 text-sm text-ink focus:border-brand-600 focus:outline-none"
          />
        )}

        <div className="ml-auto flex gap-2">
          {ansicht === 'ordner' && (
            <button
              type="button"
              onClick={() => setDialog({ presetParentId: aktuellerOrdnerId })}
              className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink"
            >
              + Ordner
            </button>
          )}
          {ansicht !== 'dateien' && (
            <button
              type="button"
              onClick={() => onNeueNotiz(ansicht === 'ordner' ? aktuellerOrdnerId : null, ansicht === 'favoriten')}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500"
            >
              + Notiz
            </button>
          )}
        </div>
      </div>

      {ansicht === 'dateien' && <DateienPanel dateien={dateien} ordner={ordner} aktuellerOrdnerId={null} />}

      {ansicht === 'favoriten' && (
        <NotizBoard
          notizen={notizen.filter((n) => n.favorit && passtZurSuche(n))}
          ordner={ordner}
          dateien={dateien}
          onOeffnen={onOeffnenNotiz}
          leerText={q ? 'Keine passenden Favoriten.' : 'Noch keine Favoriten – markiere Notizen mit ★.'}
        />
      )}

      {ansicht === 'ordner' && q && (
        <NotizBoard
          notizen={notizen.filter(passtZurSuche)}
          ordner={ordner}
          dateien={dateien}
          onOeffnen={onOeffnenNotiz}
          leerText={`Keine Treffer für „${suche.trim()}".`}
        />
      )}

      {ansicht === 'ordner' && !q && (
        <>
          {pfad.length > 0 && (
            <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm">
              <button type="button" onClick={() => setAktuellerOrdnerId(null)} className="text-ink-muted hover:text-ink">
                Wissen
              </button>
              {pfad.map((o, idx) => (
                <React.Fragment key={o.id}>
                  <span className="text-ink-muted">▸</span>
                  <button
                    type="button"
                    onClick={() => setAktuellerOrdnerId(o.id)}
                    className={idx === pfad.length - 1 ? 'font-medium text-ink' : 'text-ink-muted hover:text-ink'}
                  >
                    {o.name}
                  </button>
                </React.Fragment>
              ))}
            </nav>
          )}

          {unterordner.length > 0 && (
            <div className="mb-8">
              <h2 className="mb-3 font-mono text-xs font-semibold uppercase tracking-wider text-ink-muted">Ordner</h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {unterordner.map((o) => (
                  <OrdnerKachel
                    key={o.uuid}
                    ordner={o}
                    zaehler={zaehleOrdnerInhalt(ordner, notizen, o.id)}
                    onOeffnen={(ziel) => setAktuellerOrdnerId(ziel.id)}
                    onBearbeiten={(ziel) => setDialog({ ordner: ziel })}
                    onLoeschen={loeschenOrdner}
                  />
                ))}
              </div>
            </div>
          )}

          <div>
            <h2 className="mb-3 font-mono text-xs font-semibold uppercase tracking-wider text-ink-muted">Notizen</h2>
            <NotizBoard
              notizen={notizen.filter((n) => (n.ordnerId ?? null) === aktuellerOrdnerId)}
              ordner={ordner}
              dateien={dateien}
              onOeffnen={onOeffnenNotiz}
              onNeu={() => onNeueNotiz(aktuellerOrdnerId, false)}
            />
          </div>
        </>
      )}

      {dialog && (
        <OrdnerDialog
          ordner={dialog.ordner}
          presetParentId={dialog.presetParentId}
          alleOrdner={ordner}
          onClose={() => setDialog(null)}
        />
      )}
    </section>
  );
}
